import ThemedSelect from '../../common/ThemedSelect';
import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';

const initialForm = {
  name: '',
  locationId: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  capacityMax: '',
  capacityMin: '',
  scaleInterval: '',
  accuracyClass: 'III',
  installationType: 'Pit Mounted',
  nrcsTypeApproval: '',
  deckLength: '',
  deckWidth: '',
  numberOfLoadCells: '',
  indicatorMake: '',
  indicatorModel: '',
  indicatorSerial: '',
  installationDate: null,
  status: 'Active',
  notes: '',
};

const requiredFields = [
  'name',
  'locationId',
  'manufacturer',
  'model',
  'serialNumber',
  'capacityMax',
  'capacityMin',
  'scaleInterval',
  'accuracyClass',
  'installationType',
];

const AssetModal = ({ asset, onClose, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await api.locations.getAll();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (asset) {
      setForm({
        name: asset.name || '',
        locationId: asset.location_id || '',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        serialNumber: asset.serial_number || '',
        capacityMax: asset.capacity_max || '',
        capacityMin: asset.capacity_min || '',
        scaleInterval: asset.scale_interval || '',
        accuracyClass: asset.accuracy_class || 'III',
        installationType: asset.installation_type || 'Pit Mounted',
        nrcsTypeApproval: asset.nrcs_type_approval || '',
        deckLength: asset.deck_length || '',
        deckWidth: asset.deck_width || '',
        numberOfLoadCells: asset.num_load_cells || '',
        indicatorMake: asset.indicator_make || '',
        indicatorModel: asset.indicator_model || '',
        indicatorSerial: asset.indicator_serial || '',
        installationDate: asset.installation_date ? new Date(asset.installation_date) : null,
        status: asset.status || 'Active',
        notes: asset.notes || '',
      });
    } else {
      setForm(initialForm);
    }
    setErrors({});
  }, [asset]);

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!form[field] && form[field] !== 0) {
        const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        newErrors[field] = `${label} is required`;
      }
    });
    if (
      form.capacityMax &&
      form.capacityMin &&
      Number(form.capacityMin) >= Number(form.capacityMax)
    ) {
      newErrors.capacityMin = 'Min capacity must be less than max capacity';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        location_id: form.locationId ? Number(form.locationId) : undefined,
        manufacturer: form.manufacturer,
        model: form.model,
        serial_number: form.serialNumber,
        capacity_max: form.capacityMax ? Number(form.capacityMax) : undefined,
        capacity_min: form.capacityMin ? Number(form.capacityMin) : undefined,
        scale_interval: form.scaleInterval ? Number(form.scaleInterval) : undefined,
        accuracy_class: form.accuracyClass,
        installation_type: form.installationType,
        nrcs_type_approval: form.nrcsTypeApproval,
        deck_length: form.deckLength ? Number(form.deckLength) : undefined,
        deck_width: form.deckWidth ? Number(form.deckWidth) : undefined,
        num_load_cells: form.numberOfLoadCells ? Number(form.numberOfLoadCells) : undefined,
        indicator_make: form.indicatorMake,
        indicator_model: form.indicatorModel,
        indicator_serial: form.indicatorSerial,
        installation_date: form.installationDate
          ? toApiDateString(form.installationDate)
          : undefined,
        status: form.status,
        notes: form.notes,
      };

      if (asset?.id) {
        await api.assets.update(asset.id, payload);
        toast.success('Weighbridge asset updated');
      } else {
        await api.assets.create(payload);
        toast.success('Weighbridge asset created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (label, field, type = 'text', placeholder = '', required = false) => (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {label} {required && '*'}
      </label>
      <input
        type={type}
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        className={`w-full px-3 py-2.5 border ${errors[field] ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
        placeholder={placeholder}
      />
      {errors[field] && <p className="text-xs text-red-500 mt-1">{errors[field]}</p>}
    </div>
  );

  const renderSelect = (label, field, options, required = false) => (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {label} {required && '*'}
      </label>
      <ThemedSelect
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        className={`w-full px-3 py-2.5 border ${errors[field] ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </ThemedSelect>
      {errors[field] && <p className="text-xs text-red-500 mt-1">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {asset?.id ? 'Edit Weighbridge' : 'Add Weighbridge'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInput('Name', 'name', 'text', 'e.g. WB-001 Main Gate', true)}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Location *
              </label>
              <ThemedSelect
                value={form.locationId}
                onChange={(e) => setForm({ ...form, locationId: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.locationId ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select a location...</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.location_name}
                    {loc.location_code ? ` (${loc.location_code})` : ''}
                  </option>
                ))}
              </ThemedSelect>
              {errors.locationId && (
                <p className="text-xs text-red-500 mt-1">{errors.locationId}</p>
              )}
            </div>
            {renderInput('Manufacturer', 'manufacturer', 'text', 'e.g. Avery Weigh-Tronix', true)}
            {renderInput('Model', 'model', 'text', 'e.g. BMS HD', true)}
            {renderInput('Serial Number', 'serialNumber', 'text', 'e.g. SN-2024-001', true)}
            {renderInput('Capacity Max (kg)', 'capacityMax', 'number', 'e.g. 80000', true)}
            {renderInput('Capacity Min (kg)', 'capacityMin', 'number', 'e.g. 200', true)}
            {renderInput('Scale Interval (kg)', 'scaleInterval', 'number', 'e.g. 20', true)}
            {renderSelect('Accuracy Class', 'accuracyClass', ['III', 'II', 'I', 'IIII'], true)}
            {renderSelect(
              'Installation Type',
              'installationType',
              ['Pit Mounted', 'Surface Mounted'],
              true
            )}
            {renderInput(
              'NRCS Type Approval Number',
              'nrcsTypeApproval',
              'text',
              'e.g. TA-2024-123'
            )}
            {renderInput('Deck Length (m)', 'deckLength', 'number', 'e.g. 18')}
            {renderInput('Deck Width (m)', 'deckWidth', 'number', 'e.g. 3')}
            {renderInput('Number of Load Cells', 'numberOfLoadCells', 'number', 'e.g. 8')}
            {renderInput('Indicator Make', 'indicatorMake', 'text', 'e.g. Rinstrum')}
            {renderInput('Indicator Model', 'indicatorModel', 'text', 'e.g. R423')}
            {renderInput('Indicator Serial', 'indicatorSerial', 'text', 'e.g. IND-001')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Installation Date
              </label>
              <DatePicker
                selected={form.installationDate}
                onChange={(date) => setForm({ ...form, installationDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              />
            </div>
            {renderSelect('Status', 'status', ['Active', 'Out of Service', 'Decommissioned'])}
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              placeholder="Any additional notes about this weighbridge..."
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : asset?.id ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetModal;
